import { useEffect, useState } from "react";
import { Pet } from "../../../common/types";
import { utils } from "web3";
import { blacklistedAddresses } from "../../helpers/helpers";
import { getResolvedEnsAddress } from "../../helpers/utils";

type WalletEntry = {
  label: string;
  address: string;
};

function fetchWalletHistory(): WalletEntry[] {
  const stored = localStorage.getItem("walletAddresses");
  if (!stored) return [];

  try {
    const parsed = JSON.parse(stored);
    return parsed.map((entry: any) => {
      if (typeof entry === "string") {
        return { label: entry, address: entry };
      }
      return {
        label: entry.label ?? entry.address ?? "",
        address: entry.address ?? entry.label ?? "",
      };
    });
  } catch (err) {
    console.error("Failed to parse walletAddresses", err);
    return [];
  }
}

export function WalletSelector({
  pets,
  walletAddress,
  setWalletAddress,
  setIsLoading,
  isLoading,
  isPhone,
  isDarkMode,
}: {
  pets: Pet[];
  walletAddress: string;
  setWalletAddress: (address: string, label?: string) => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  isPhone: boolean;
  isDarkMode: boolean;
}) {
  const [walletAddressHistory, setWalletAddressHistory] = useState<WalletEntry[]>(fetchWalletHistory());
  const [displayWalletHistory, setDisplayWalletHistory] = useState(false);
  const [onRemoveButton, setOnRemoveButton] = useState(false);
  const [sortKey, setSortKey] = useState<keyof Pet>("score");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [inputValue, setInputValue] = useState(walletAddress);

  function addWalletAddressToHistory(label: string, address: string) {
    let updated = walletAddressHistory.filter((entry) => entry.address !== address);
    updated.unshift({ label, address });
    setWalletAddressHistory(updated);
    localStorage.setItem("walletAddresses", JSON.stringify(updated));
  }

  function removeWalletAddressFromHistory(addressToRemove: string) {
    const updated = walletAddressHistory.filter((entry) => entry.address !== addressToRemove);
    setWalletAddressHistory(updated);
    localStorage.setItem("walletAddresses", JSON.stringify(updated));
  }

  useEffect(() => {
    setSortKey("score");
    setSortOrder("desc");
  }, [walletAddress]);

  async function fetchPets(input: string, labelOverride?: string) {
  let newWalletAddress = input.trim();
  let label = labelOverride ?? newWalletAddress;

  if (newWalletAddress.endsWith(".eth")) {
    setIsLoading(true);
    const resolved = await getResolvedEnsAddress(newWalletAddress);
    if (!resolved) {
      console.warn("> Invalid .eth name or not resolved");
      setIsLoading(false);
      return;
    }
    newWalletAddress = resolved;
  }

  if (newWalletAddress.length === 40 && !newWalletAddress.startsWith("0x")) {
    newWalletAddress = "0x" + newWalletAddress;
  }

  if (newWalletAddress && newWalletAddress.length === 42 && newWalletAddress.startsWith("0x")) {
    try {
      const checksummedAddress = utils.toChecksumAddress(newWalletAddress);
      if (blacklistedAddresses.has(checksummedAddress)) return;

      setWalletAddress(checksummedAddress, label);
      setInputValue(label); // ← this ensures ENS stays in search bar
      addWalletAddressToHistory(label, checksummedAddress);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  }
}


  useEffect(() => {
    const walletAddresses = fetchWalletHistory();
    setWalletAddressHistory(walletAddresses);
    if (walletAddresses.length === 1) {
      fetchPets(walletAddresses[0].address);
    } else {
      setInputValue("");
    }
  }, []);

  const handleSearch = async (address?: string) => {
    setDisplayWalletHistory(false);
    setOnRemoveButton(false);
    address ? fetchPets(address) : fetchPets(inputValue);
  };

  useEffect(() => {
  const ensHistory = localStorage.getItem("walletAddresses");
  if (!ensHistory || !walletAddress) return;

  try {
    const parsed = JSON.parse(ensHistory);
    const match = parsed.find((entry: any) => {
      const addr = entry.address?.toLowerCase?.() ?? "";
      return addr === walletAddress.toLowerCase();
    });

    if (match && match.label) {
      setInputValue(match.label);
    } else {
      setInputValue(walletAddress);
    }
  } catch (e) {
    console.error("Failed to parse wallet history for ENS label sync", e);
    setInputValue(walletAddress);
  }
}, [walletAddress]);


  return (
    <div
      className={`relative mr-2 mt-5 flex h-8 w-60 flex-col items-center ${isDarkMode ? "bg-darkblack" : "bg-green"}`}
      onMouseLeave={() => {
        setDisplayWalletHistory(false);
        setOnRemoveButton(false);
      }}
    >
      <div
        className={`z-50 flex h-full w-full flex-row items-center rounded-md border-2 ${isDarkMode ? "border-darkgreen bg-darkblue" : "border-brown bg-white"}`}
      >
        {isPhone && pets.length !== 0 && isLoading ? (
          <img src={isDarkMode ? "/imgs/refreshDark.svg" : "/imgs/refresh.svg"} alt="Refreshing" className="spin z-20 ml-2 h-4 w-4" />
        ) : (
          <img src={isDarkMode ? "/imgs/searchDark.svg" : "/imgs/search.svg"} alt="Search" className="z-20 ml-2 h-4 w-4" onClick={() => handleSearch()} />
        )}
        <input
          type="text"
          placeholder="wallet address or ens..."
          value={inputValue}
          className={`w-full rounded-md pl-2 focus:outline-none ${isDarkMode ? " bg-darkblue text-white placeholder-white" : "bg-white placeholder-brown"}`}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => walletAddressHistory.length > 1 && setDisplayWalletHistory(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch(inputValue);
            }
          }}
          onMouseEnter={() => walletAddressHistory.length > 1 && setDisplayWalletHistory(true)}
        />
      </div>

      {displayWalletHistory && walletAddressHistory.length > 0 && (
        <div className={`full absolute top-0 z-40 flex w-full cursor-default flex-col rounded-md border-2 border-t-0 ${isDarkMode ? "border-darkgreen bg-darkblue" : "border-brown bg-white"}`}>
          {walletAddressHistory
            .filter((entry) => entry.address !== walletAddress)
            .map((entry) => (
              <div
                key={entry.address}
                className={`flex h-full items-end justify-between border-b-2 p-1 text-center first:h-[66px] first:rounded-t-md first:border-t-0 last:border-b-0 ${isDarkMode ? "border-darkgreen text-darkgreen hover:bg-darkblack hover:text-white" : "border-brown hover:bg-brown hover:text-white"}`}
                onClick={() => fetchPets(entry.address, entry.label)}
              >
                <span className="truncate">
                  {entry.label.endsWith(".eth") ? entry.label : entry.address}
                </span>
                <div
                  className={`rounded-md border-2 px-2 ${isDarkMode ? "border-darkgreen bg-darkblue text-darkgreen [box-shadow:inset_0_-1px_#37b764] hover:bg-darkgreen hover:text-white" : "border-brown bg-white text-brown [box-shadow:inset_0_-1px_#342e2e] hover:bg-green hover:text-white"}`}
                  onMouseEnter={() => setOnRemoveButton(true)}
                  onMouseLeave={() => setOnRemoveButton(false)}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeWalletAddressFromHistory(entry.address);
                  }}
                >
                  <img src="/imgs/x.svg" alt="" className="h-6 w-6" />
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
